import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    IconButton,
    Collapse,
    styled,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import debounce from 'lodash.debounce';
import CustomIcon from "../../../../../Components/CustomIcon";
import CartLoader from "../../../../../Helper/Loaders/CartLoader";

const NodeItem = styled(Stack)(({ selected, highlight, isLeaf, onClick }) => ({
    backgroundColor: selected ? "#EFF0FF" : "inherit",
    borderRadius: 4,
    cursor: onClick ? "pointer" : "auto",
    "&:hover": {
        backgroundColor: "#F9F9F9",
    },
    "& .icon-button": {
        width: 24,
        height: 24,
        background: "none",
    },
    "& .MuiTypography-root": {
        color: (selected || highlight) && !isLeaf ? "#4D44D8" : "inherit",
        fontWeight: selected ? 500 : "inherit",
    },
}));


const ItemRowCheckBoxLabel = styled(FormControlLabel)(({ theme }) => ({
    display: 'block',
    marginLeft: '0px',

    '& .MuiCheckbox-root': {
        padding: 0,
        marginBottom: '8px',
        marginRight: '8px',
        marginTop: '8px',
    },

    '& .MuiCheckbox-root.Mui-checked': {
        color: '#494949 !important',
    },

    '& .MuiCheckbox-root.Mui-disabled': {
        color: '#989898 !important',
    },

}));

const getAllChildIds = (node) => {
    let ids = [];
    if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
            ids.push(child.id);
            ids = ids.concat(getAllChildIds(child));
        });
    }
    return ids;
};

const getNodesAtLevel = (nodes, targetLevel, currentLevel = 0) => {
    let result = [];
    for (const node of nodes) {
        if (currentLevel === targetLevel) {
            result.push(node);
        } else if (node.children) {
            result = result.concat(getNodesAtLevel(node.children, targetLevel, currentLevel + 1));
        }
    }
    return result;
};

const RenderComparisonTabs = ({
    data = [],
    onChangeNode = () => { },
    hierarchyLevel,
    batteryConfig,
    maxLevel = 5,
}) => {
    const [openNodes, setOpenNodes] = useState({});
    const [selectedIds, setSelectedIds] = useState([]);
    const [shouldDisableCheckbox, setShouldDisableCheckbox] = useState(false);

    const debouncedOnChange = useCallback(debounce((updatedIds) => {
        onChangeNode(updatedIds);
    }, 800), []);


    const handleToggle = (event, id) => {
        event.stopPropagation();
        setOpenNodes((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const toggleCheckbox = (id) => {
        const updatedIds = selectedIds.includes(id)
            ? selectedIds.filter((sid) => sid !== id)
            : [...selectedIds, id];
        setSelectedIds(updatedIds);
        debouncedOnChange(updatedIds);
    };

    const getCountForInitialSelection = () => {
        let defaultCount = 20
        if (!hierarchyLevel) {
            return defaultCount
        }

        if (hierarchyLevel == 'cell') {
            return parseInt(batteryConfig.cellCountPerModule) || defaultCount
        }

        if (hierarchyLevel == 'module') {
            return parseInt(batteryConfig.moduleCountPerPack) || defaultCount
        }

        if (hierarchyLevel == 'pack') {
            return parseInt(batteryConfig.packCount) || defaultCount
        }

        return defaultCount
    }

    const renderNode = (node, level = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isOpen = openNodes[node.id] || false;
        const isLeaf = !hasChildren;
        const isCheckboxLevel = level === maxLevel;

        const childrenAtMaxLevel = (node.children || []).filter((child) => level + 1 === maxLevel);
        const childIds = childrenAtMaxLevel.map((child) => child.id);
        const isAllSelected = childIds.every((id) => selectedIds.includes(id));
        const isPartiallySelected = childIds.some((id) => selectedIds.includes(id)) && !isAllSelected;

        const handleLocalSelectAll = (e) => {
            const checked = e.target.checked;
            const updated = checked
                ? Array.from(new Set([...selectedIds, ...childIds]))
                : selectedIds.filter((id) => !childIds.includes(id));
            setSelectedIds(updated);
            onChangeNode(updated);
        };

        return (
            <Box key={node.id} ml={(level + 1) * 1} mt={0.5} className="node-item">
                <NodeItem
                    direction="row"
                    alignItems="center"
                    isLeaf={isLeaf}
                    onClick={undefined}
                >
                    {hasChildren && level < maxLevel ? (
                        <>
                            <IconButton className="icon-button" onClick={(event) => handleToggle(event, node.id)}>
                                <CustomIcon name="primary-arrow-icon" rotate={isOpen ? 90 : 0} />
                            </IconButton>
                            <Typography variant="fw400s12c8F8F8F">{node.label}</Typography>
                        </>
                    ) : isCheckboxLevel ? (
                        <ItemRowCheckBoxLabel
                            key={node.id}
                            control={<Checkbox
                                sx={{ color: '#494949' }}
                                disabled={shouldDisableCheckbox}
                                checked={selectedIds.includes(node.id)}
                                onChange={() => toggleCheckbox(node.id)}

                            />}
                            label={<Typography variant={'fw400s12c494949'}> {node.label} </Typography>}
                        />
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", borderLeft: "1px solid #BBBBBB", mt: -0.5 }} className="last-view"
                        >
                            <Box className="center-update" sx={{ position: "relative", left: "-0.79px", bottom: "4px" }}>
                                <CustomIcon color="#BBBBBB" height="50px" name="last-node-level-icon" />
                            </Box>
                            <Typography variant="fw400s12c494949">{node.label}</Typography>
                        </Box>
                    )}
                </NodeItem>

                {hasChildren && isOpen && level < maxLevel && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box className="node-children-container">
                            {childrenAtMaxLevel.length > 0 && (
                                <ItemRowCheckBoxLabel
                                    sx={{ marginLeft: `${(level + 2) * 8}px`, mr: 0, mt: 0.5 }}
                                    key={`${node.id}_select_all`}
                                    control={
                                        <Checkbox
                                            sx={{ color: '#494949' }}
                                            checked={isAllSelected}
                                            indeterminate={isPartiallySelected}
                                            onChange={handleLocalSelectAll}
                                        />
                                    }
                                    label={<Typography variant={'fw500s12lh24c494949'}> All </Typography>}
                                />
                            )}
                            {node.children.map((child) => renderNode(child, level + 1))}
                        </Box>
                    </Collapse>
                )}
            </Box>
        );
    };


    useEffect(() => {
        if (!data || data.length === 0) return;

        const selectionCount = getCountForInitialSelection()

        if (selectionCount == 1) {
            setShouldDisableCheckbox(true)
        }

        const newSelected = getNodesAtLevel(data, maxLevel).map(n => n.id);
        const defaultIds = newSelected.slice(0, selectionCount)
        setSelectedIds(defaultIds);
        onChangeNode(defaultIds);

        const traverseToLevel = (node, currentLevel = 0) => {
            if (currentLevel < maxLevel) {
                openNodes[node.id] = true;
                (node.children || []).forEach(child => traverseToLevel(child, currentLevel + 1));
            }
        };

        data.forEach(root => traverseToLevel(root));
        setOpenNodes(openNodes);

    }, [maxLevel, data]);

    if (!data || data.length === 0) {
        return <CartLoader />;
    }

    return (
        <Box sx={{ overflow: "auto", maxHeight: 'calc(100vh - 250px)', pb: 2 }}>
            {data.map((node) => renderNode(node))}
        </Box>
    );
};

export default RenderComparisonTabs;
